<script>
	import { Button, Col, Row } from "@sveltestrap/sveltestrap";
	import { form, field } from "svelte-forms";
	import { required } from "svelte-forms/validators";
	import { navigate } from "svelte-routing";
	import { onMount, tick } from "svelte";
	import Swal from "sweetalert2";
	import SveltyPicker from "svelty-picker";

	import { convertDateTime } from "../../services/helpers";
	import { getBlackout, saveBlackout } from "../../services/blackout";
	import {
		errorMessage,
		loading,
		createBlackoutData,
		blackoutCarData,
		carSelection,
		editBlackoutData,
		initialEditBlackout,
	} from "../../store/store";
	import { extractErrorMessage } from "../../utils/misc";

	import PageHeader from "../../components/PageHeader/PageHeader.svelte";
	import ErrorMessageHandler from "../../components/ErrorMessageHandler/ErrorMessageHandler.svelte";
	import CustomInput from "../../components/CustomInput/CustomInput.svelte";
	import Loader from "../../components/Loader/Loader.svelte";
	import ImportedCsvTable from "./Manage/ImportedCsvTable.svelte";

	export let blackoutId;

	// ── Form fields ──────────────────────────────────────────────────────────
	let files;
	let csvUrl = "";

	const storedData = $createBlackoutData;
	let blackoutStartDate = field(
		"blackoutStartDate",
		storedData?.blackoutStartDate ?? "",
		[required()]
	);
	let blackoutEndDate = field(
		"blackoutEndDate",
		storedData?.blackoutEndDate ?? "",
		[required()]
	);
	let reason = field("reason", storedData?.reason ?? "", [required()]);
	let csvFile = field("csvFile", "");

	const myForm = form(blackoutStartDate, blackoutEndDate, reason, csvFile);

	// ── Loading state ─────────────────────────────────────────────────────────
	const returningFromCarSelection =
		!!blackoutId &&
		$initialEditBlackout !== 0 &&
		String($editBlackoutData?.id) === String(blackoutId);

	let fetching = !!blackoutId && !returningFromCarSelection;

	// ── Helpers ───────────────────────────────────────────────────────────────
	function restoreFormFields(data) {
		$blackoutStartDate = {
			...$blackoutStartDate,
			value: data.blackoutStartDate,
		};
		$blackoutEndDate = { ...$blackoutEndDate, value: data.blackoutEndDate };
		$reason = { ...$reason, value: data.reason };
	}

	function navigateToCarSelection() {
		createBlackoutData.set({
			reason: $reason.value,
			blackoutStartDate: $blackoutStartDate.value,
			blackoutEndDate: $blackoutEndDate.value,
			csvFile: csvUrl,
		});
		navigate("/blackout-management/create/select-car");
	}

	// ── Lifecycle ─────────────────────────────────────────────────────────────
	onMount(async () => {
		if (!blackoutId) {
			editBlackoutData.set({ id: "", stats: false });
			if ($createBlackoutData?.blackoutStartDate) {
				restoreFormFields($createBlackoutData);
			}
			return;
		}

		if (returningFromCarSelection) {
			restoreFormFields($createBlackoutData);
			initialEditBlackout.set(0);
			return;
		}

		// Edit mode — fresh load
		blackoutCarData.set("");
		try {
			const data = await getBlackout(blackoutId);
			const cars = data.blackoutVehicles.map((car) => ({
				id: car.vehicleId,
				vehicleModelName: car.modelName,
				plateNumber: car.plateNumber,
				vehicleMakeName: car.makeName,
				status: car.status,
				activeFrom: car.activeFrom,
				activeTo: car.activeTo,
				carParkId: car.carparkId,
				carParkName: car.carparkName,
			}));
			const formData = {
				id: data.id,
				blackoutStartDate: convertDateTime(data.blackoutStartDate),
				blackoutEndDate: convertDateTime(data.blackoutEndDate),
				blackoutVehicles: data.blackoutVehicles,
				reason: data.reason,
				status: data.status,
			};
			createBlackoutData.set(formData);
			editBlackoutData.set({ id: data.id, stats: true });
			blackoutCarData.set(cars);
			fetching = false;
			await tick();
			restoreFormFields(formData);
		} catch (error) {
			extractErrorMessage(error);
			fetching = false;
		}
	});

	// ── Submission ────────────────────────────────────────────────────────────
	async function handleSubmit() {
		const blackoutVehicles = Array.isArray($blackoutCarData)
			? $blackoutCarData.map((car) => ({ vehicleId: car.id }))
			: [];

		loading.set(true);
		try {
			const res = await saveBlackout({
				blackoutStartTime: new Date($blackoutStartDate.value),
				blackoutEndTime: new Date($blackoutEndDate.value),
				reason: $reason.value,
				blackoutVehicles,
				csvFile: $createBlackoutData?.csvFile || csvUrl || null,
				id: blackoutId,
			});

			if (res.status === 200) {
				Swal.fire({
					title: "Success",
					text: `Blackout ${blackoutId ? "updated" : "created"} successfully`,
					type: "success",
					confirmButtonText: "OK",
					customClass: { confirmButton: "custom-ok-button" },
				}).then((result) => {
					if (result.isConfirmed) {
						createBlackoutData.set("");
						blackoutCarData.set("");
						carSelection.set([]);
						navigate("/blackout-management");
					}
				});
			} else {
				Swal.fire({
					title: "Error",
					text: res.data.error_msg,
					type: "error",
					confirmButtonText: "OK",
				});
			}
		} catch (error) {
			extractErrorMessage(error);
		} finally {
			loading.set(false);
		}
	}

	// ── Validation ────────────────────────────────────────────────────────────
	$: endDateMin = (() => {
		const startVal = $blackoutStartDate.value
			? new Date($blackoutStartDate.value)
			: null;
		if (blackoutId) return startVal ?? undefined;
		const today = new Date();
		return startVal
			? new Date(Math.max(today.getTime(), startVal.getTime()))
			: today;
	})();

	$: timeValidation =
		!!$blackoutEndDate.value &&
		!!$blackoutStartDate.value &&
		new Date($blackoutEndDate.value) < new Date($blackoutStartDate.value);

	// ── CSV handling ──────────────────────────────────────────────────────────
	function handleCSV(e) {
		const file = e.target.files[0];
		if (!file) {
			csvUrl = "";
			return;
		}
		const reader = new FileReader();
		reader.readAsDataURL(file);
		reader.onload = (evt) => {
			csvUrl = evt.target.result;
		};
	}
</script>

<Row>
	<Col>
		{#if fetching}
			<Loader />
		{:else}
			<div>
				<PageHeader
					title={blackoutId ? "Edit Blackout" : "Create Blackout"}
				/>
				<ErrorMessageHandler message={$errorMessage} />
				<Row>
					<Col xs={3}>
						<label for="blackoutStartDate" class="d-block mt-3">
							<span> <b>Blackout Start Date*</b> </span>
						</label>
					</Col>
				</Row>
				<Row>
					<Col xs={3}>
						<SveltyPicker
							inputClasses="form-control getgo input {$myForm.hasError(
								'blackoutStartDate.required'
							)
								? 'border border-danger'
								: ''}"
							format="yyyy-mm-dd hh:ii"
							startDate={blackoutId ? undefined : new Date()}
							placeholder="YYYY-MM-DD HH:MM"
							bind:value={$blackoutStartDate.value}
						/>
						{#if $myForm.hasError("blackoutStartDate.required")}
							<div>
								<p class="error-text">
									Blackout start date is required
								</p>
							</div>
						{/if}
					</Col>
				</Row>

				<Row>
					<Col xs={3}>
						<label for="blackoutEndDate" class="d-block mt-3">
							<span> <b>Blackout End Date*</b> </span>
						</label>
					</Col>
				</Row>
				<Row>
					<Col xs={3}>
						<SveltyPicker
							inputClasses="form-control getgo input {$myForm.hasError(
								'blackoutEndDate.required'
							) || timeValidation
								? 'border border-danger'
								: ''}"
							format="yyyy-mm-dd hh:ii"
							placeholder="YYYY-MM-DD HH:MM"
							startDate={endDateMin}
							bind:value={$blackoutEndDate.value}
						/>
						{#if $myForm.hasError("blackoutEndDate.required")}
							<div>
								<p class="error-text">
									Blackout end date is required
								</p>
							</div>
						{:else if timeValidation}
							<div>
								<p class="error-text">
									Blackout end date cannot be earlier than
									start date
								</p>
							</div>
						{/if}
					</Col>
				</Row>

				<Row>
					<Col xs={2}>
						<label for="reason" class="d-block mt-3">
							<span> <b>Reason*</b> </span>
						</label>
					</Col>
				</Row>
				<Row>
					<Col xs={5}>
						<CustomInput
							type="text"
							bind:value={$reason.value}
							error={$myForm.hasError("reason.required")}
							errorText="Reason is required"
						/>
					</Col>
				</Row>
				{#if !$createBlackoutData.csvFile}
					<Row>
						<Col xs={2}>
							<label for="csvInput" class="d-block mt-3">
								<span> <b>CSV File</b> {$csvFile.value}</span>
							</label>
						</Col>
					</Row>
					<Row>
						<Col xs={5}>
							<input
								id="csvInput"
								class="csv-file-input"
								type="file"
								multiple={false}
								bind:files
								on:change={(e) => handleCSV(e)}
							/>
						</Col>
					</Row>
				{/if}
				<Row>
					<Col class="d-flex justify-content-end gap-3">
						<Button
							class="themeButton primary"
							disabled={!$reason.value ||
								!$blackoutStartDate.value ||
								!$blackoutEndDate.value ||
								timeValidation ||
								csvUrl}
							on:click={navigateToCarSelection}>Add Car</Button
						>

						{#if !$loading}
							<Button
								class="themeButton primary"
								disabled={!$reason.value ||
									!$blackoutStartDate.value ||
									!$blackoutEndDate.value ||
									timeValidation ||
									(!$blackoutCarData && csvUrl == "")}
								on:click={handleSubmit}>Save</Button
							>
						{:else if $loading}
							<Button
								class="themeButton primary px-4"
								type="button"
								disabled
							>
								<output
									class="spinner-grow spinner-grow-sm"
									aria-hidden="true"
								></output>
								Loading...
							</Button>
						{/if}
					</Col>
				</Row>
				<Col class="my-2">
					<ImportedCsvTable />
				</Col>
			</div>
		{/if}
	</Col>
</Row>

<style>
	:global(.custom-ok-button) {
		background: #19f9b9 !important;
		color: #011851 !important;
		outline: 0 !important;
		box-shadow: unset !important;
		border: unset !important;
	}

	:global(.csv-file-input) {
		width: 100%;
		height: 48px;
		border-radius: 8px;
		border: 1px solid #d3d3d3;
		background: #fff;
		margin: 3px 0;
		padding: 0;
		overflow: hidden;
		cursor: pointer;
	}

	:global(.csv-file-input::file-selector-button) {
		height: 100%;
		padding: 8px 15px;
		background: var(--fds-primary);
		color: var(--fds-on-primary);
		font-weight: bold;
		border: 0;
		border-radius: 0;
		cursor: pointer;
		margin-right: 10px;
	}
</style>
